import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Get payment details from URL parameters
      const payment_id = searchParams.get('payment_id');
      const payment_request_id = searchParams.get('payment_request_id');
      const payment_status = searchParams.get('payment_status');

      if (!payment_id || !payment_request_id) {
        setStatus('failed');
        toast.error('Invalid payment response');
        return;
      }

      // Verify payment with backend
      const response = await axios.post(
        `${API}/orders/verify`,
        {
          payment_id,
          payment_request_id
        },
        { withCredentials: true }
      );

      if (response.data.success && response.data.payment_verified) {
        setStatus('success');
        setPaymentDetails(response.data.payment_details);
        
        // Clear pending order from sessionStorage
        sessionStorage.removeItem('pending_order_id');
        
        toast.success('Payment successful!');
        
        // Redirect to profile/orders after 3 seconds
        setTimeout(() => {
          navigate('/profile?tab=orders');
        }, 3000);
      } else {
        setStatus('failed');
        toast.error('Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('failed');
      toast.error('Failed to verify payment');
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'loading' && (
          <div className="glass-effect p-8 rounded-xl text-center">
            <Loader2 className="w-16 h-16 text-red-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="glass-effect p-8 rounded-xl text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-gray-400 mb-6">
              Your order has been confirmed. Thank you for your purchase!
            </p>
            
            {paymentDetails && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Payment ID:</span>
                    <span className="font-mono">{paymentDetails.payment_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount Paid:</span>
                    <span className="font-semibold text-green-500">₹{paymentDetails.amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className="text-green-500 font-semibold">{paymentDetails.status}</span>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 mb-4">
              Redirecting to your orders in 3 seconds...
            </p>
            
            <button
              onClick={() => navigate('/profile?tab=orders')}
              className="btn-primary w-full py-3"
            >
              View My Orders
            </button>
          </div>
        )}

        {status === 'failed' && (
          <div className="glass-effect p-8 rounded-xl text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
            <p className="text-gray-400 mb-6">
              Unfortunately, your payment could not be processed. Please try again.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full py-3"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/cart')}
                className="w-full py-3 border border-white/20 rounded-lg hover:bg-white/5 transition"
              >
                Back to Cart
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;